import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Modulos, Papel } from "@/app/navegacao";

export interface Membro {
  empresaId: string;
  papel: Papel;
  empresaNome: string;
  modulos: Modulos;
}

export type EstadoSessao =
  | { situacao: "carregando" }
  | { situacao: "deslogado" }
  | { situacao: "sem_empresa"; userId: string }
  | { situacao: "pronto"; userId: string; membro: Membro };

interface Contexto {
  estado: EstadoSessao;
  session: Session | null;
  recarregarMembro: () => Promise<void>;
  sair: () => Promise<void>;
}

const AuthCtx = createContext<Contexto | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [membro, setMembro] = useState<Membro | null>(null);
  const [carregandoSessao, setCarregandoSessao] = useState(true);
  /**
   * Comeca `null`: "ainda nao sabemos". Enquanto for null com sessao ativa, o
   * estado e `carregando`.
   *
   * Sem isso existia uma janela em que a sessao ja tinha chegado mas o membro
   * nao, o guard lia "sem empresa", mandava para /comecar e o onboarding
   * rebatia para /atendimento SEM a query string. Resultado visivel: a tela de
   * onboarding piscava a cada refresh e o link direto para o Kanban abria a
   * lista.
   */
  const [membroResolvidoPara, setMembroResolvidoPara] = useState<string | null>(null);

  useEffect(() => {
    // A ordem importa: registrar o listener antes de ler a sessao, senao um
    // login que termina entre as duas chamadas passa despercebido.
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      setCarregandoSessao(false);
    });
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setCarregandoSessao(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const userId = session?.user.id ?? null;

  const carregarMembro = useMemo(
    () => async (uid: string | null) => {
      if (!uid) {
        setMembro(null);
        setMembroResolvidoPara(null);
        return;
      }
      const { data, error } = await supabase
        .from("empresa_membros")
        .select("empresa_id, papel, empresas(nome, modulo_loja, modulo_servicos)")
        .eq("user_id", uid)
        .eq("ativo", true)
        .maybeSingle();

      if (error || !data) {
        setMembro(null);
      } else {
        const empresa = data.empresas as unknown as {
          nome: string;
          modulo_loja: boolean;
          modulo_servicos: boolean;
        } | null;
        setMembro({
          empresaId: data.empresa_id as string,
          papel: data.papel as Papel,
          empresaNome: empresa?.nome ?? "",
          modulos: {
            loja: empresa?.modulo_loja ?? true,
            servicos: empresa?.modulo_servicos ?? false,
          },
        });
      }
      setMembroResolvidoPara(uid);
    },
    [],
  );

  useEffect(() => {
    void carregarMembro(userId);
  }, [userId, carregarMembro]);

  const estado: EstadoSessao = useMemo(() => {
    if (carregandoSessao) return { situacao: "carregando" };
    if (!session) return { situacao: "deslogado" };
    // Ainda nao terminamos de resolver o membro DESTE usuario.
    if (membroResolvidoPara !== session.user.id) return { situacao: "carregando" };
    if (!membro) return { situacao: "sem_empresa", userId: session.user.id };
    return { situacao: "pronto", userId: session.user.id, membro };
  }, [carregandoSessao, membroResolvidoPara, session, membro]);

  const valor = useMemo<Contexto>(
    () => ({
      estado,
      session,
      recarregarMembro: () => carregarMembro(userId),
      sair: async () => {
        await supabase.auth.signOut();
        setMembro(null);
      },
    }),
    [estado, session, carregarMembro, userId],
  );

  return <AuthCtx.Provider value={valor}>{children}</AuthCtx.Provider>;
}

export function useAuth(): Contexto {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  return ctx;
}

export function useSessao(): EstadoSessao {
  return useAuth().estado;
}

/** Atalho para quando a tela ja esta dentro do ProtectedRoute. */
export function useEmpresa(): Membro {
  const { estado } = useAuth();
  if (estado.situacao !== "pronto") {
    throw new Error("useEmpresa fora de rota protegida");
  }
  return estado.membro;
}
